import {
	RiArrowLeftRightLine,
	RiKey2Line,
	RiLogoutBoxLine,
	RiShieldUserLine,
} from '@remixicon/react';
import { type ReactNode, useRef, useState } from 'react';

import * as history from '@shared/router/location';

import { Button } from '@playbook/atoms/button';
import { GroupHeader } from '@playbook/atoms/group-header';
import { MenuItem } from '@playbook/atoms/menu-item';
import { AvatarHeader } from '@playbook/compositions/avatar-header';
import { ClubSelectorItem } from '@playbook/compositions/club-selector-item';
import { ClubMenuGroup } from '@playbook/modules/club-menu';
import {
	PopoutMenu,
	type PopoutMenuHandle,
} from '@playbook/modules/popout-menu';

import type { Account } from '@portal/pkg/api/types';
import * as routes from '@portal/pkg/routes';
import { useSession } from '@portal/pkg/session';

function toDisplayName(account: Nullable<Account>): string {
	const name = `${account?.firstName ?? ''} ${account?.lastName ?? ''}`.trim();

	return name || account?.email || '';
}

export function AccountMenu(): ReactNode {
	const {
		account,
		organization,
		availableOrganizations,
		selectOrganization,
		logout,
	} = useSession();

	const menu = useRef<PopoutMenuHandle>(null);
	const [choosing, setChoosing] = useState<Nullable<number>>(null);

	function go(href: string): void {
		menu.current?.close();
		history.push(href);
	}

	function signOut(): void {
		menu.current?.close();
		logout();
		history.push(routes.Login());
	}

	async function choose(organizationId: number): Promise<void> {
		setChoosing(organizationId);

		try {
			await selectOrganization(organizationId);

			go(routes.Keys());
		} finally {
			setChoosing(null);
		}
	}

	const isCurrentClub = (id: number) => id === organization?.id;

	return (
		<PopoutMenu
			ref={menu}
			appearFrom="right"
			renderTriggerWith={(popoverTarget) => (
				<Button
					variant="secondary"
					label={organization?.name ?? 'Choose club'}
					leadingIcon={<RiShieldUserLine />}
					trailingIcon={<RiArrowLeftRightLine />}
					popoverTarget={popoverTarget}
				/>
			)}>
			<ClubMenuGroup
				renderHeaderWith={
					<div style={{ paddingTop: '10px' }}>
						<AvatarHeader
							label={toDisplayName(account)}
							avatarUrl={account?.profileImageUrl || undefined}
						/>
					</div>
				}>
				<MenuItem
					label="Manage keys"
					leadingSlot={<RiKey2Line />}
					onClick={() => go(routes.Keys())}
				/>
				<MenuItem
					variant="destructive"
					label="Log out"
					leadingSlot={<RiLogoutBoxLine />}
					onClick={signOut}
				/>
			</ClubMenuGroup>

			<ClubMenuGroup renderHeaderWith={<GroupHeader label="Organizations" />}>
				{availableOrganizations.map((available) => (
					<ClubSelectorItem
						key={available.id}
						label={available.name}
						selected={isCurrentClub(available.id)}
						description={
							isCurrentClub(available.id) ? 'Active organization' : undefined
						}
						onClick={
							choosing === null ? () => void choose(available.id) : undefined
						}
					/>
				))}
			</ClubMenuGroup>
		</PopoutMenu>
	);
}
